import React, { useState, useEffect, useMemo } from 'react';
import { isFoundationLarge } from 'utils/breakpoints';
import { useViewportWidth } from 'decorators/withViewportWidth';
import useIsDesktop from 'hooks/useIsDesktop';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { bindActionCreators } from '@reduxjs/toolkit';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { concat, get, isEmpty } from 'lodash';
import loadable from '@loadable/component';

import { fetchSearchResults } from 'store/search/slice';
import { createBooking, createLead } from 'store/bookings/slice';
import { facilityPageSelector } from 'store/selectors/facilities';
import { getFacilityHoursAndDirection } from 'store/facilities/slice';
import {
	getLocalPhoneNumberByZipCode,
	getTollfreePhoneNumber,
	getLocalPhoneNumberByCityState,
} from 'store/pageMeta/slice';
import { OONFacilityPage } from 'pages/OONFacilityPage';
import FacilityPageV2 from 'pages/FacilityPageV2/FacilityPageV2';

import { withPageViewTracking } from 'components/core/library/tracking-utils';
import { withHoursAndDirectionsHandlers } from 'decorators/facility';
import { useWebPageSchema, withSearchHandler } from 'decorators';
import { Button } from 'components/core/Button';
import { Expand } from 'components/core/Expand';
import { Image } from 'components/core/Image';
import { H3, H4 } from 'components/core/Headline';
import { Paragraph } from 'components/core/Paragraph';
import { Text } from 'components/core/Text';
import { Group, Page } from 'components/core/Page';
import { FacilityDetails } from 'components/facility/FacilityDetails';
import { IconText } from 'components/core/IconText';
import { FacilityImageNotAvailable } from 'components/facility/ImageNotAvailable';

const ImageCarousel = loadable(() => import(/* webpackChunkName: "ImageCarousel" */ 'components/core/ImageCarousel'));
import { FacilityBreadcrumbs } from 'components/facility/FacilityBreadcrumbs';
import { FacilityFees } from 'components/facility/FacilityFees';
import { FacilityGroupedAmenityList } from 'components/facility/FacilityGroupedAmenityList';
import { FacilityHours } from 'components/facility/FacilityHours';
import { FilterableUnitList } from 'components/facility/FilterableUnitList';
import { FacilityReviews } from 'components/facility/FacilityReviews';
import { BackToTop } from 'components/core/BackToTop';
import { AmexPromo } from 'components/partner/amex';
import { ModalLink } from 'components/core/ModalLink';

const UnitSizeGuide = loadable(() => import(/* webpackChunkName: "UnitSizeGuide" */ 'components/unit/UnitSizeGuide'));
import { VehicleStorage, SelfStorage, Clock, Plus } from 'components/svg';
import { UnitList, BookableUnitDetails } from 'components/unit/UnitList';
import { scrollToElement } from 'lib/helpers';
import { HowItWorksFeatures } from 'components/facility/HowItWorksFeatures/index';
import { ScarcityBanner } from 'components/search/ScarcityBanner';
import { BadgeList } from 'components/facility/BadgeList';
import { FacilityRow } from 'components/search/FacilityRow';
import { GiftCardBanner } from 'components/Banners/GiftCardBanner';
import GiftCardModalLink from 'components/giftcard/GiftCardModalLink/GiftCardModalLink';
import { FacilityUnitMicroData } from 'components/facility/FacilityUnitMicroData';

import { NewFourOhFourPage } from 'pages/NewFourOhFourPage';
import { InactiveFacilityPage } from 'pages/FacilityPage';
import './facilityPage.scss';
import { TrackingClient } from '@sparefoot/tracking-client';
import { PAGE_TYPES } from '@sparefoot/tracking-events';
import { trackCTAClick } from '../../components/core/library/tracking-utils/tracking/clickTrackingUtils';
import { encode } from 'querystring';
import { useDecision } from '@optimizely/react-sdk';
import { OPTIMIZELY_FLAGS } from 'config/optimizely';
import { trackDecision } from 'lib/optimizelyDebug';

const withProps = (propsOrFn) => (Component) => {
	const WithProps = (props) => {
		const extra =
			typeof propsOrFn === 'function' ? propsOrFn(props) : propsOrFn;
		return (
			<Component
				{...props}
				{...extra}
			/>
		);
	};
	return WithProps;
};

const branch = (test, left, right) => (Component) => (props) => {
	if (test(props)) {
		const Left = left(Component);
		return <Left {...props} />;
	}
	if (right) {
		const Right = right(Component);
		return <Right {...props} />;
	}
	return <Component {...props} />;
};

const renderComponent = (X) => () => X;

const renderNothing = () => () => null;

function withFacilityPageState(Component) {
	function FacilityPageWithState(ownProps) {
		const dispatch = useDispatch();
		const selectorResult = useSelector((state) => {
			const base = facilityPageSelector(state, ownProps);
			return {
				...base,
				baseUrl: state.app && state.app.baseUrl,
				googleMapsKey: state.app && state.app.googleMapsKey,
				queryParams: ownProps.location
					? Object.fromEntries(
							new URLSearchParams(ownProps.location.search),
						)
					: {},
			};
		}, shallowEqual);
		const actions = useMemo(
			() => bindActionCreators({
				fetchSearchResults,
				createBooking,
				createLead,
				getFacilityHoursAndDirection,
				getLocalPhoneNumberByZipCode,
				getLocalPhoneNumberByCityState,
				getTollfreePhoneNumber,
			}, dispatch),
			[dispatch],
		);
		return <Component {...ownProps} {...selectorResult} actions={actions} />;
	}
	FacilityPageWithState.displayName = 'FacilityPageWithState';
	return FacilityPageWithState;
}

export const enhance = (Component) =>
	withFacilityPageState(
		branch(
			(props) => !props.facility,
			renderNothing,
		)(
			branch(
				(props) => props.facility.isOutOfNetwork,
				renderComponent(OONFacilityPage),
			)(
				branch(
					(props) => props.facility.isInactive,
					(C) =>
						withProps({ statusCode: 410 })(
							renderComponent(NewFourOhFourPage)(C),
						),
				)(
					withPageViewTracking(PAGE_TYPES.FACILITY)(
						withHoursAndDirectionsHandlers(
							withProps(({ queryParams, facility }) => ({
								searchType: get(
									queryParams,
									'searchType',
									'storage',
								),
								unitSizeFilters: concat(
									[],
									get(queryParams, 'unitSizeFilters', []),
								),
								scrollToReviews: () =>
									scrollToElement('.facility-reviews'),
								scrollToUnits: () =>
									scrollToElement('#facility-units'),
								facilityBadges: facility.badges,
								facilitiy: facility,
							}))(
								withSearchHandler(
									branch(
										(props) => props.facilityIsInactive,
										renderComponent(
											InactiveFacilityPage,
										),
									)(Component),
								),
							),
						),
					),
				),
			),
		),
	);

export const FacilityPageV1 = (props) => {
	const isDesktop = useIsDesktop(isFoundationLarge);

	return (
		<Page
			className="facility-page in-network-facility-page"
			pageType={props.pageType}
		>
			{props.scarcityShow && <ScarcityBanner />}
			<div>
				<div className="content-container">
					<FacilityBreadcrumbs facility={props.facility} />
				</div>
				<div className="main-content">
					<div className="row content-container">
						<div className="split-column column-left">
							{!props.hasOnlineMoveIns &&
								!props.facilityIsInactive && (
									<div className="risk-free-heading">
										<Paragraph
											strong
											size="extra-large"
											className="headline"
										>
											Make a risk-free reservation
										</Paragraph>
										<Paragraph
											light
											size="small"
										>
											Lock in your low rate. There's no
											risk &ndash; easily cancel or change
											your reservation at any time.
										</Paragraph>
									</div>
								)}
							{!isEmpty(props.facilityBadges) && !isDesktop && (
								<div className="facility-badge-list">
									<Group>
										<BadgeList
											facilityBadges={
												props.facility.badges.filter(
													(badge) =>
														!(
															badge ===
																'giftcard' &&
															props.sf15309Variation !==
																'variation'
														),
												) // sf_15309_giftcard_incentives
											}
											giftCardValue={
												props.facility?.giftCardOffer
													?.giftCardValue
											}
										/>
									</Group>
								</div>
							)}

							<FacilityDetails
								facilityName={props.facility.name}
								facilityId={props.facility.id}
								facilityZipCode={props.facility.location.zip}
								locationText={props.facility.location.text}
								reviewAverage={get(
									props.facility,
									'reviewMetadata.average',
								)}
								reviewCount={get(
									props.facility,
									'reviewMetadata.count',
								)}
								facilityPhoneNumber={
									props.phone.localSparephone
								}
								unformattedFacilityPhoneNumber={
									props.phone.localSparephoneUnformatted
								}
								onGetHoursAndDirections={
									props.actions.getFacilityHoursAndDirection
								}
								getLocalPhoneNumberByZipCode={
									props.actions.getLocalPhoneNumberByZipCode
								}
								getLocalPhoneNumberByCityState={
									props.actions.getLocalPhoneNumberByCityState
								}
							/>
						</div>
						{!isEmpty(props.facility) &&
							!isEmpty(props.facility.badges) &&
							isDesktop && (
								<div className="split-column column-right facility-badge-list desktop">
									<Group>
										<BadgeList
											facilityBadges={
												props.facility.badges.filter(
													(badge) =>
														!(
															badge ===
																'giftcard' &&
															props.sf15309Variation !==
																'variation'
														),
												) // sf_15309_giftcard_incentives
											}
											giftCardValue={
												props.facility?.giftCardOffer
													?.giftCardValue
											}
										/>
									</Group>
								</div>
							)}
					</div>
					<div className="row content-container">
						<div className="split-column column-left">
							{props.facility.description && (
								<div className="facility-description-container">
									<H3
										strong
										size="medium"
										className="section-heading"
									>
										About {props.facility.name}
									</H3>
									<Expand
										showing={props.descriptionExpanded}
										showTeaser
									>
										<div className="facility-description">
											<Paragraph
												size="small"
												light
											>
												<span
													// eslint-disable-next-line react/no-danger
													dangerouslySetInnerHTML={{
														__html: decodeURIComponent(
															props.facility
																.description,
														).replace(
															/\n/gi,
															'<br />',
														),
													}}
												/>
											</Paragraph>
										</div>
									</Expand>
									<Button
										link
										preContent={<Plus />}
										onClick={
											props.toggleDescriptionExpanded
										}
										className="show-more-button"
									>
										<Text size="small">
											Read{' '}
											{props.descriptionExpanded
												? 'less'
												: 'more'}{' '}
											description
										</Text>
									</Button>
								</div>
							)}
							<div className="image-and-alerts">
								<div className="facility-image-container">
									{!!props.images.length && (
											<ImageCarousel
												className="facility-image-carousel"
												images={props.images}
												preload={true}
											/>
									)}
									{!props.images.length && (
										<FacilityImageNotAvailable size="large" />
									)}
								</div>
								{!props.facilityIsInactive && (
									<IconText
										className="facility-urgency-alert"
										icon={Clock}
										type="urgent"
									>
										We are experiencing high demand in this
										area. Book today to secure your storage
										unit.
									</IconText>
								)}
								{get(
									props.facility,
									'messaging.amexActive',
								) && (
									<AmexPromo className="facilityPromotion" />
								)}
							</div>
						</div>
						<div className="split-column column-right">
							<div className="facility-information-container">
								<H3
									strong
									size="medium"
									className="section-heading"
								>
									Features & amenities
								</H3>
								<Expand
									showing={props.informationPanelExpanded}
									showTeaser
								>
									<div className="expandable-facility-information">
										<FacilityGroupedAmenityList
											groupedAmenities={
												props.facility.groupedAmenities
											}
											hasVehicles={
												!isEmpty(props.vehicleUnits)
											}
											searchType={props.searchType}
										/>
										<div className="expandable-facility-hours">
											<FacilityHours
												accessHours={
													props.facility.hours.access
														.text
												}
												officeHours={
													props.facility.hours.office
														.text
												}
											/>
										</div>
										<FacilityFees
											adminFee={props.facility.fees.admin}
											deposit={
												props.facility.fees.deposit
											}
											insurance={
												props.facility.fees.insurance
											}
										/>
									</div>
								</Expand>
								<Button
									link
									preContent={<Plus />}
									onClick={props.toggleInformationExpanded}
									className="show-more-button"
								>
									<Text size="small">
										See{' '}
										{props.informationPanelExpanded
											? 'fewer'
											: 'more'}{' '}
										features & amenities
									</Text>
								</Button>
							</div>
						</div>
					</div>
					<div className="row content-container small-flush reservation-block">
						<div className="split-column column-left">
							{props.hasOnlineMoveIns ? (
								<div className="secondary-risk-free small-flush-padding">
									<H4
										strong
										className="omi-section-heading"
									>
										Rent online today or <br /> make a
										risk-free reservation
									</H4>
									<div className="omi-section">
										<Paragraph>
											There's two easy ways to secure a
											unit at this storage facility:
										</Paragraph>
										<ol>
											<li>
												You can complete your rental
												process online today and go
												straight to your unit when you
												plan to move in.
												<Paragraph
													strong
													className="omi-number-point-separator"
												>
													OR
												</Paragraph>
											</li>
											<li>
												You can make a reservation today
												with no payment until you sign
												your lease at your move-in date.
											</li>
										</ol>
									</div>
								</div>
							) : (
								<div className="secondary-risk-free small-flush-padding">
									<H4
										strong
										size="small"
										className="section-heading"
									>
										Make a risk-free reservation to lock in
										your low rate
									</H4>
									<Paragraph light>
										No payment until move in. Easily change
										your reservation online.
									</Paragraph>
								</div>
							)}
							<div
								className="unit-lists-container"
								id="facility-units"
							>
								<H4
									strong
									size="small"
									className="section-heading small-flush-padding"
								>
									Available storage units
								</H4>
								<FacilityUnitMicroData
									storageUnits={props.storageUnits}
									vehicleUnits={props.vehicleUnits}
									facilityName={props.facility.name}
									facilityUrl={props.facility.url.facility}
									facilityImageUrl={
										props.facility.images?.[0]?.src || ''
									}
									baseUrl={props.baseUrl}
								/>
								<div
									className={classnames({
										'vehicle-first':
											props.searchType === 'vehicle',
									})}
								>
									{props.storageUnits &&
										!!props.storageUnits.length && (
											<div key="storage-units-group">
												<div
													className="unit-list-container"
													id="unit-list"
												>
													<div
														// eslint-disable-next-line max-len
														className="unit-list-title unit-list-modal-link small-flush-padding"
													>
														<ModalLink
															text="Learn about storage unit sizes"
															content={
																<UnitSizeGuide />
															}
														/>
													</div>
													<div className="unit-list-header">
														<IconText
															className="icon-storage"
															icon={SelfStorage}
														>
															Storage units
														</IconText>
													</div>
													{props.facility
														.giftCardOffer
														?.giftCardValue && (
														<GiftCardBanner
															className="facility-gift-card-banner"
															content={
																<GiftCardModalLink />
															}
														/>
													)}

													<FilterableUnitList
														searchParams={
															props.queryParams
														}
														units={
															props.storageUnits
														}
														unitSizeFilters={
															props.unitSizeFilters
														}
														searchType={
															props.searchType
														}
														facility={
															props.facility
														}
														phone={props.phone}
														unitDetailsComponentProps={(
															unit,
														) => ({
															handleToggleUnitReservationDrawer:
																props.handleToggleUnitReservationDrawer,
															onCreateBooking:
																props.actions
																	.createBooking,
															storageType:
																'selfStorage',
															reservationFormShowing:
																props.selectedUnit ===
																	unit.id &&
																props.selectedStorageType ===
																	'selfStorage',
															facilityImages:
																props.images,
															reservationFormRef:
																props.reservationFormRef,
														savedFormValues:
																props.savedFormValues,
														onFormValuesChange:
																props.onFormValuesChange,
														})}
													/>
												</div>
											</div>
										)}
									{props.vehicleUnits &&
										!!props.vehicleUnits.length && (
											<div key="vehicle-units-group">
												<div
													className="vehicle-units"
													id="vehicle-unit-list"
												>
													<div className="unit-list-header">
														<IconText
															className="icon-vehicle"
															icon={
																VehicleStorage
															}
														>
															Vehicle units
														</IconText>
													</div>
													<UnitList
														units={
															props.vehicleUnits
														}
														facility={
															props.facility
														}
														showUnitSizeFilterAlert={
															false
														}
														phone={props.phone}
														unitDetailsComponent={
															BookableUnitDetails
														}
														unitDetailsComponentProps={(
															unit,
														) => ({
															handleToggleUnitReservationDrawer:
																props.handleToggleUnitReservationDrawer,
															onCreateBooking:
																props.actions
																	.createBooking,
															storageType:
																'vehicle',
															reservationFormShowing:
																props.selectedUnit ===
																	unit.id &&
																props.selectedStorageType ===
																	'vehicle',
															facilityImages:
																props.images,
															facility:
																props.facility,
															reservationFormRef:
																props.reservationFormRef,
														savedFormValues:
																props.savedFormValues,
														onFormValuesChange:
																props.onFormValuesChange,
														})}
													/>
												</div>
											</div>
										)}
								</div>
							</div>
						</div>
						<div className="split-column column-right">
							<div className="small-flush-padding">
								<HowItWorksFeatures />
							</div>
						</div>
					</div>
					<div className="row single-column content-container">
						<div className="split-column column-left">
							<div className="facility-map">
								<H4
									strong
									size="medium"
									className="section-heading"
								>
									Facility map
								</H4>
								<Image
									className="static-map"
									src={props.facility.staticMapUrl}
									alt="facilityMap"
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div
				className="reviews-content-wrapper"
				id="reviews"
			>
				<div className="content-container">
					<FacilityReviews
						reviews={props.facility.reviews}
						reviewAverage={get(
							props.facility,
							'reviewMetadata.average',
							0,
						)}
						reviewCount={get(
							props.facility,
							'reviewMetadata.count',
							0,
						)}
						ratingCounts={get(
							props.facility,
							'reviewMetadata.ratingCounts',
						)}
						pros={get(props.facility, 'reviewMetadata.pros')}
						cons={get(props.facility, 'reviewMetadata.cons')}
						recommendedCount={get(
							props.facility,
							'reviewMetadata.recommendedCount',
						)}
						recommendedRate={get(
							props.facility,
							'reviewMetadata.recommendedRate',
						)}
						facilityId={props.facility.id}
						facilityUrl={props.facility.url.facility}
						fullFacilityUrl={props.fullFacilityUrl}
						currentReviewPage={props.reviewPage}
						images={get(props, 'images', []).map(
							(image) => image.src,
						)}
						facilityName={get(props, 'facility.name')}
						locationText={props.facility.location.text}
					/>
				</div>
			</div>
			{props.nearbyFacilities && props.nearbyFacilities.length > 0 && (
				<div className="content-container">
					<FacilityRow
						className="nearby-facilities-page"
						location={props.facility.location}
						facilities={props.nearbyFacilities}
						headline="Nearby Storage Facilities"
						headerSize="medium"
					/>
				</div>
			)}
			<BackToTop />
			<script
				key="schema"
				type="application/ld+json"
				// eslint-disable-next-line react/no-danger
				dangerouslySetInnerHTML={{ __html: props.pageSchema }}
			/>
			<script
				key="schema"
				type="application/ld+json"
				// eslint-disable-next-line react/no-danger
				dangerouslySetInnerHTML={{ __html: props.offerCatalogSchema }}
			/>
			<script
				key="schema"
				type="application/ld+json"
				// eslint-disable-next-line react/no-danger
				dangerouslySetInnerHTML={{ __html: props.mapSchema }}
			/>
		</Page>
	);
};

export const FacilityPage = ({ images = [], actions = {}, ...restProps }) => {
	const { viewportWidth } = useViewportWidth();
	const baseProps = { images, actions, ...restProps, viewportWidth };
	const webPageSchemaProps = useWebPageSchema(baseProps);
	const props = { ...baseProps, ...webPageSchemaProps };
	const [selectedUnit, setSelectedUnit] = useState(null);
	const [selectedStorageType, setSelectedStorageType] = useState(null);
	const [descriptionExpanded, setDescriptionExpanded] = useState(false);
	const [informationPanelExpanded, setInformationPanelExpanded] =
		useState(false);
	const [savedFormValues, setSavedFormValues] = useState({});
	const reservationFormRef = React.useRef(null);

	// Use SDK decision hook directly instead of Redux state
	const [decision] = useDecision(OPTIMIZELY_FLAGS.FACILITY_PAGE_REDESIGN.key);
	trackDecision(OPTIMIZELY_FLAGS.FACILITY_PAGE_REDESIGN.key, decision);
	const isVariation = decision?.enabled || false;

	const handleToggleUnitReservationDrawer = (unit, storageType) => {
		// There is no selected unit when the user opens the drawer; only closing, so this only sends when
		// a user clicks the CTA to book a unit, not closing the drawer
		if (!selectedUnit) {
			let clickUrl = `${props?.match?.url}?${encode(props?.queryParams)}`;

			// Only fire CTA click when drawer is opened
			trackCTAClick(
				'render-reservation-form-button',
				'Continue Button',
				'Continue',
				clickUrl,
			);

			let { facility } = props;

			TrackingClient.trackFacilityUnitSelected({
				facility: {
					id: unit?.facilityId,
					name: facility?.name,
					location: facility?.location?.text,
				},
				last_unit_added_date: new Date(),
				unit: {
					currency: 'usd',
					id: unit?.id,
					variant_size: unit?.sizeText,
					price: unit?.price?.text,
					quantity: 1,
				},
				unit_id: unit.id,
				unit_promo: unit.promotion,
			});
		}

		// reset the movein date whenever someone opens a new reservation form
		if (reservationFormRef.current) {
			reservationFormRef.current.setValue('moveInDate', '');
		}

		const newSelectedUnit =
			selectedUnit === unit.id && selectedStorageType === storageType
				? null
				: unit.id;
		const newSelectedStorageType =
			selectedUnit === unit.id && selectedStorageType === storageType
				? null
				: storageType;
		setSelectedUnit(newSelectedUnit);
		setSelectedStorageType(newSelectedStorageType);
	};

	const toggleDescriptionExpanded = () => {
		setDescriptionExpanded((prev) => !prev);
	};

	const toggleInformationExpanded = () => {
		setInformationPanelExpanded((prev) => !prev);
	};

	useEffect(() => {
		if (props.facility) {
			const { facility } = props;
			TrackingClient.trackFacilityViewed({
				facility_badge: `${facility.badges}`,
				facility_id: facility.id,
				facility_name: facility.name,
				facility_promo: facility.promotion,
				facility_rank: '', // TODO: Potentially use queryParam to get rank from search page?
			});
		}
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	if (!props.facility) {
		return (
			<Page
				className="facility-page"
				pageType={props.pageType}
			/>
		);
	}

	const facilityBadges = props.facilityBadges ? [...props.facilityBadges] : [];
	const showOmiBadge = facilityBadges.includes('omi');
	if (showOmiBadge) {
		facilityBadges[facilityBadges.indexOf('omi')] = 'omiBadge';
	}

	const stateProps = {
		selectedUnit,
		selectedStorageType,
		descriptionExpanded,
		informationPanelExpanded,
		handleToggleUnitReservationDrawer,
		toggleDescriptionExpanded,
		toggleInformationExpanded,
		reservationFormRef,
		savedFormValues,
		onFormValuesChange: setSavedFormValues,
	};

	if (isVariation) {
		return (
			<FacilityPageV2
				{...props}
				{...stateProps}
			/>
		);
	}
	return (
		<FacilityPageV1
			{...props}
			{...stateProps}
		/>
	);
};

const facilityPagePropTypes = {
	actions: PropTypes.shape({
		createBooking: PropTypes.func,
		getFacilityHoursAndDirection: PropTypes.func,
		getLocalPhoneNumberByZipCode: PropTypes.func.isRequired,
		getLocalPhoneNumberByCityState: PropTypes.func.isRequired,
		setHeaderCityDrilldownData: PropTypes.func,
	}),
	baseUrl: PropTypes.string,
	facility: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		name: PropTypes.string,
		description: PropTypes.string,
		badges: PropTypes.array,
		closures: PropTypes.array,
		giftCardOffer: PropTypes.shape({
			giftCardValue: PropTypes.number,
		}),
		groupedAmenities: PropTypes.array,
		hours: PropTypes.shape({
			access: PropTypes.shape({ text: PropTypes.string }),
			office: PropTypes.shape({ text: PropTypes.string }),
		}),
		fees: PropTypes.shape({
			admin: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			deposit: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			insurance: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		}),
		images: PropTypes.arrayOf(PropTypes.shape({ src: PropTypes.string })),
		location: PropTypes.shape({
			text: PropTypes.string,
			zip: PropTypes.string,
		}),
		promotion: PropTypes.string,
		reviews: PropTypes.object,
		staticMapUrl: PropTypes.string,
		url: PropTypes.shape({
			facility: PropTypes.string,
		}),
	}),
	facilityBadges: PropTypes.arrayOf(PropTypes.string),
	facilityIsInactive: PropTypes.bool,
	viewportWidth: PropTypes.number,
	fullFacilityUrl: PropTypes.string,
	handleToggleUnitReservationDrawer: PropTypes.func,
	hasOnlineMoveIns: PropTypes.bool,
	images: PropTypes.arrayOf(
		PropTypes.shape({
			src: PropTypes.string.isRequired,
			alt: PropTypes.string,
		}),
	),
	informationPanelExpanded: PropTypes.bool,
	mapSchema: PropTypes.string,
	match: PropTypes.shape({
		url: PropTypes.string,
	}),
	nearbyFacilities: PropTypes.arrayOf(PropTypes.object),
	offerCatalogSchema: PropTypes.string,
	onFormValuesChange: PropTypes.func,
	pageSchema: PropTypes.string,
	pageType: PropTypes.string,
	phone: PropTypes.shape({
		localSparephone: PropTypes.string,
		localSparephoneUnformatted: PropTypes.string,
	}),
	queryParams: PropTypes.object,
	reservationFormRef: PropTypes.object,
	reviewPage: PropTypes.number,
	savedFormValues: PropTypes.object,
	scarcityShow: PropTypes.bool,
	searchType: PropTypes.string,
	selectedStorageType: PropTypes.string,
	selectedUnit: PropTypes.number,
	sf11786Variation: PropTypes.string,
	sf15309Variation: PropTypes.string,
	storageUnits: PropTypes.arrayOf(PropTypes.object),
	toggleDescriptionExpanded: PropTypes.func,
	toggleInformationExpanded: PropTypes.func,
	descriptionExpanded: PropTypes.bool,
	unitSizeFilters: PropTypes.arrayOf(PropTypes.string),
	vehicleUnits: PropTypes.arrayOf(PropTypes.object),
};

FacilityPageV1.propTypes = facilityPagePropTypes;
FacilityPage.propTypes = facilityPagePropTypes;

export default enhance(FacilityPage);
